import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import { MaterialReactTable, MRT_ColumnDef } from "material-react-table";
import { getReportParameters, getReports, runReport } from "./reportingApi";
import { LegacyReportParameter, LegacyReportResult } from "./types";

const anchorPattern = /<a\s+(?:[^>]*?\s+)?href=(["'])(.*?)\1[^>]*>(.*?)<\/a>/i;

export default function ReportingPage() {
  const [reports, setReports] = useState<string[]>([]);
  const [selectedReport, setSelectedReport] = useState("");
  const [parameters, setParameters] = useState<LegacyReportParameter[]>([]);
  const [parameterValues, setParameterValues] = useState<Record<string, string>>({});
  const [result, setResult] = useState<LegacyReportResult | null>(null);
  const [loadingReports, setLoadingReports] = useState(true);
  const [loadingParameters, setLoadingParameters] = useState(false);
  const [running, setRunning] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    getReports()
      .then(setReports)
      .catch(() => setError("Unable to load reports."))
      .finally(() => setLoadingReports(false));
  }, []);

  useEffect(() => {
    if (!selectedReport) {
      setParameters([]);
      setParameterValues({});
      setResult(null);
      return;
    }

    setLoadingParameters(true);
    setError("");
    setResult(null);
    getReportParameters(selectedReport)
      .then((items) => {
        setParameters(items);
        setParameterValues({});
      })
      .catch(() => setError("Unable to load report criteria."))
      .finally(() => setLoadingParameters(false));
  }, [selectedReport]);

  const reportColumns = useMemo<MRT_ColumnDef<Record<string, unknown>>[]>(() => {
    return (result?.columns ?? []).map((column) => ({
      id: column,
      header: column,
      accessorFn: (row) => row[column],
      Cell: ({ cell }) => <ReportCell value={cell.getValue()} />,
      muiTableHeadCellProps: {
        sx: {
          fontSize: 12,
          fontWeight: 700,
          whiteSpace: "nowrap"
        }
      },
      muiTableBodyCellProps: {
        sx: {
          fontSize: 12,
          whiteSpace: "nowrap"
        }
      }
    }));
  }, [result?.columns]);

  const reportRows = result?.rows ?? [];

  const criteria = useMemo(
    () =>
      parameters
        .map((parameter) => ({
          label: parameter.label,
          value: parameterValues[parameter.name]
        }))
        .filter((item) => item.value),
    [parameters, parameterValues]
  );

  const handleRun = async () => {
    if (!selectedReport) {
      return;
    }

    setRunning(true);
    setError("");
    try {
      setResult(await runReport(selectedReport, parameterValues));
    } catch (err) {
      const message = axios.isAxiosError(err)
        ? (err.response?.data as { message?: string } | undefined)?.message
        : undefined;
      setError(message || "Unable to run report.");
    } finally {
      setRunning(false);
    }
  };

  const handleClear = () => {
    setParameterValues({});
    setResult(null);
    setError("");
  };

  const handleExport = async () => {
    if (!result || result.rows.length === 0) {
      return;
    }

    setExporting(true);
    setError("");
    try {
      await exportReportToExcel(selectedReport, result);
    } catch {
      setError("Unable to export report.");
    } finally {
      setExporting(false);
    }
  };

  return (
    <Box sx={{ p: { xs: 1.5, md: 2 }, maxWidth: "100%" }}>
      <Stack spacing={1.25}>
        <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" spacing={1.25}>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 600 }}>
              Reporting
            </Typography>
          </Box>
          <Stack direction="row" spacing={1} alignItems="center">
            <Button
              variant="contained"
              startIcon={running ? <CircularProgress color="inherit" size={16} /> : <PlayArrowIcon />}
              disabled={!selectedReport || running || loadingParameters}
              onClick={handleRun}
            >
              Run Report
            </Button>
            <Button
              variant="outlined"
              startIcon={exporting ? <CircularProgress color="inherit" size={16} /> : <DownloadIcon />}
              disabled={exporting || !result || result.rows.length === 0}
              onClick={handleExport}
            >
              Export To Excel
            </Button>
            <Button variant="text" startIcon={<RestartAltIcon />} onClick={handleClear}>
              Clear
            </Button>
          </Stack>
        </Stack>

        {error && <Alert severity="error">{error}</Alert>}

        <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 1 }}>
          <Stack direction={{ xs: "column", lg: "row" }} spacing={1.25} alignItems={{ lg: "flex-start" }}>
            <FormControl sx={{ minWidth: { xs: "100%", lg: 360 } }} size="small">
              <InputLabel id="report-select-label">Select report</InputLabel>
              <Select
                labelId="report-select-label"
                label="Select report"
                value={selectedReport}
                disabled={loadingReports}
                onChange={(event) => setSelectedReport(event.target.value)}
              >
                {reports.map((report) => (
                  <MenuItem key={report} value={report}>
                    {report}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {loadingParameters ? (
              <Stack direction="row" spacing={1} alignItems="center" sx={{ minHeight: 40 }}>
                <CircularProgress size={18} />
                <Typography variant="body2" color="text.secondary">
                  Loading criteria
                </Typography>
              </Stack>
            ) : (
              <Box sx={{ display: "flex", flex: 1, flexWrap: "wrap", gap: 1, alignItems: "flex-start" }}>
                {parameters.map((parameter) => (
                  <ParameterField
                    key={parameter.name}
                    parameter={parameter}
                    value={parameterValues[parameter.name] ?? ""}
                    onChange={(value) =>
                      setParameterValues((current) => ({
                        ...current,
                        [parameter.name]: value
                      }))
                    }
                  />
                ))}
              </Box>
            )}
          </Stack>
        </Paper>

        {criteria.length > 0 && (
          <Paper variant="outlined" sx={{ px: 1.5, py: 1, borderRadius: 1 }}>
            <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
              Report Criteria
            </Typography>
            <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
              {criteria.map((item) => (
                <Typography key={`${item.label}-${item.value}`} variant="body2" color="text.secondary">
                  <Box component="span" sx={{ fontWeight: 600, color: "text.primary" }}>
                    {item.label}:
                  </Box>{" "}
                  {item.value}
                </Typography>
              ))}
            </Stack>
          </Paper>
        )}

        {running && <ResultsLoading />}

        {!running && result && (
          <MaterialReactTable
            enableStickyHeader
            columns={reportColumns}
            data={reportRows}
            enableColumnActions={false}
            enableColumnOrdering={false}
            enableColumnResizing={false}
            enableFacetedValues
            enableGlobalFilter
            enablePagination={false}
            enableSorting={false}
            autoResetPageIndex={false}
            initialState={{
              density: "compact"
            }}
            muiTablePaperProps={{
              variant: "outlined",
              sx: {
                borderRadius: 1,
                boxShadow: "none"
              }
            }}
            muiTableContainerProps={{
              sx: {
                maxHeight: "calc(100vh - 260px)"
              }
            }}
            muiTableBodyProps={{
              sx: {
                "& .MuiTableRow-root:hover .MuiTableCell-root": {
                  backgroundColor: "inherit !important"
                }
              }
            }}
            muiTableBodyRowProps={({ row }) => ({
              sx: {
                backgroundColor: row.index % 2 === 0 ? "action.hover" : "background.paper"
              }
            })}
            renderTopToolbarCustomActions={() => (
              <Typography variant="subtitle2" sx={{ alignSelf: "center", pl: 1 }}>
                Total Files: {result.rows.length}
              </Typography>
            )}
          />
        )}
      </Stack>
    </Box>
  );
}

function ResultsLoading() {
  return (
    <Paper
      variant="outlined"
      sx={{
        alignItems: "center",
        borderRadius: 1,
        display: "flex",
        justifyContent: "center",
        minHeight: 220
      }}
    >
      <Stack spacing={1} alignItems="center">
        <CircularProgress size={28} />
        <Typography variant="body2" color="text.secondary">
          Running report
        </Typography>
      </Stack>
    </Paper>
  );
}

function ParameterField({
  parameter,
  value,
  onChange
}: {
  parameter: LegacyReportParameter;
  value: string;
  onChange: (value: string) => void;
}) {
  if (parameter.type === "dropdown") {
    const selectedValues = value ? value.split(",") : [];

    return (
      <Box sx={{ position: "relative", width: { xs: "100%", sm: parameter.options.length <= 2 ? 220 : 280 } }}>
        <Typography
          variant="caption"
          component="label"
          htmlFor={`${parameter.name}-listbox`}
          sx={{
            bgcolor: "background.paper",
            color: "text.secondary",
            left: 10,
            lineHeight: 1,
            px: 0.5,
            position: "absolute",
            top: -6,
            zIndex: 1
          }}
        >
          {parameter.label}
        </Typography>
        <Box
          component="select"
          id={`${parameter.name}-listbox`}
          multiple
          aria-multiselectable="true"
          value={selectedValues}
          onChange={(event) => {
            const nextValues = Array.from(event.currentTarget.selectedOptions, (option) => option.value);
            onChange(nextValues.join(","));
          }}
          sx={{
            bgcolor: "background.paper",
            border: 1,
            borderColor: "divider",
            borderRadius: 1,
            color: "text.primary",
            display: "block",
            font: "inherit",
            fontSize: 13,
            height: parameter.options.length <= 2 ? 70 : 126,
            lineHeight: 1.4,
            outline: "none",
            overflowY: "auto",
            px: 0,
            pb: 0.5,
            pt: 1.5,
            width: "100%",
            "& option": {
              px: 1,
              py: 0
            },
            "&:focus": {
              borderColor: "primary.main",
              boxShadow: (theme) => `0 0 0 1px ${theme.palette.primary.main}`
            }
          }}
        >
          {parameter.options.map((option) => (
            <option key={option} value={option} title={option}>
              {option}
            </option>
          ))}
        </Box>
      </Box>
    );
  }

  return (
    <TextField
      size="small"
      type={parameter.type === "date" ? "date" : "text"}
      label={parameter.label}
      value={value}
      InputLabelProps={parameter.type === "date" ? { shrink: true } : undefined}
      onChange={(event) => onChange(event.target.value)}
      sx={{ width: { xs: "100%", sm: 240 } }}
    />
  );
}

function ReportCell({ value }: { value: unknown }) {
  const text = value == null ? "" : String(value);
  const anchorMatch = text.match(anchorPattern);

  if (anchorMatch) {
    return (
      <a href={anchorMatch[2]} target="_blank" rel="noreferrer">
        {stripHtml(anchorMatch[3]) || "Link Bulletin"}
      </a>
    );
  }

  return <>{stripHtml(text)}</>;
}

function stripHtml(value: string) {
  return value.replace(/<[^>]*>/g, "");
}

async function exportReportToExcel(reportName: string, result: LegacyReportResult) {
  const ExcelJS = await import("exceljs");
  const workbook = new ExcelJS.Workbook();
  workbook.creator = "TitleQ Reporting";
  workbook.created = new Date();
  workbook.modified = new Date();

  addReportWorksheet(workbook, sanitizeWorksheetName(reportName) || "Report", result.columns, result.rows);

  if (reportName === "Sales-Open & Closed Orders" && result.columns.includes("Sales Rep")) {
    const rowsBySalesRep = new Map<string, Record<string, unknown>[]>();

    result.rows.forEach((row) => {
      const salesRep = stripHtml(row["Sales Rep"] == null ? "" : String(row["Sales Rep"])).trim() || "Blank";
      rowsBySalesRep.set(salesRep, [...(rowsBySalesRep.get(salesRep) ?? []), row]);
    });

    Array.from(rowsBySalesRep.entries())
      .sort(([left], [right]) => left.localeCompare(right))
      .forEach(([salesRep, rows]) => addReportWorksheet(workbook, salesRep, result.columns, rows));
  }

  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `${sanitizeFileName(reportName) || "report"}.xlsx`;
  link.click();
  URL.revokeObjectURL(url);
}

function addReportWorksheet(
  workbook: import("exceljs").Workbook,
  requestedName: string,
  columns: string[],
  rows: Record<string, unknown>[]
) {
  const worksheet = workbook.addWorksheet(getUniqueWorksheetName(workbook, requestedName), {
    views: [{ state: "frozen", ySplit: 1 }]
  });

  worksheet.addRow(columns);
  rows.forEach((row) => worksheet.addRow(columns.map((column) => getExcelCellValue(row[column]))));

  worksheet.autoFilter = {
    from: { row: 1, column: 1 },
    to: { row: Math.max(rows.length + 1, 1), column: columns.length }
  };

  worksheet.eachRow((row, rowNumber) => {
    row.font = { name: "Arial", size: 10 };
    row.alignment = { horizontal: "left", vertical: "top" };

    if (rowNumber === 1) {
      row.font = { name: "Arial", size: 10, bold: true, underline: true, color: { argb: "FFFFFFFF" } };
      row.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FF00B0D8" } };
    } else if (rowNumber % 2 === 0) {
      row.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFEFF3FB" } };
    }

    row.eachCell((cell) => {
      cell.border = {
        bottom: { style: "thin", color: { argb: "FFD9E2EC" } },
        left: { style: "thin", color: { argb: "FFE6EDF3" } },
        right: { style: "thin", color: { argb: "FFE6EDF3" } },
        top: { style: "thin", color: { argb: "FFD9E2EC" } }
      };

      if (typeof cell.value === "object" && cell.value && "hyperlink" in cell.value) {
        cell.font = { name: "Arial", size: 10, color: { argb: "FF0563C1" }, underline: true };
      }
    });
  });

  columns.forEach((column, index) => {
    const values = rows.map((row) => getExcelCellText(row[column]));
    const maxLength = Math.max(column.length, ...values.map((value) => value.length));
    worksheet.getColumn(index + 1).width = Math.min(Math.max(maxLength + 2, 10), 60);
  });
}

function getExcelCellValue(value: unknown) {
  if (value == null) {
    return "";
  }

  if (typeof value === "number" || typeof value === "boolean" || value instanceof Date) {
    return value;
  }

  const text = String(value);
  const anchorMatch = text.match(anchorPattern);
  if (anchorMatch) {
    return {
      text: stripHtml(anchorMatch[3]) || "Link Bulletin",
      hyperlink: anchorMatch[2]
    };
  }

  return stripHtml(text);
}

function getExcelCellText(value: unknown) {
  const excelValue = getExcelCellValue(value);
  if (typeof excelValue === "object" && excelValue && "text" in excelValue) {
    return String(excelValue.text);
  }

  return excelValue == null ? "" : String(excelValue);
}

function getUniqueWorksheetName(workbook: import("exceljs").Workbook, requestedName: string) {
  const baseName = sanitizeWorksheetName(requestedName) || "Report";
  let name = baseName;
  let suffix = 2;

  while (workbook.getWorksheet(name)) {
    const suffixText = ` ${suffix}`;
    name = `${baseName.slice(0, 31 - suffixText.length)}${suffixText}`;
    suffix += 1;
  }

  return name;
}

function sanitizeWorksheetName(value: string) {
  return value.replace(/[\]\\/*?:[\]]/g, " ").replace(/\s+/g, " ").trim().slice(0, 31);
}

function sanitizeFileName(value: string) {
  return value.replace(/[^a-z0-9]+/gi, "-").replace(/^-|-$/g, "");
}
